  import { Checkbox, Wrap, WrapItem } from '@chakra-ui/react'
  import _ from 'lodash'
  import type { RefObject } from 'react'
  import React, { useCallback, useState, useEffect, useRef } from 'react'
  import { useTranslation } from 'react-i18next'
  import { getDefaultExpression } from '../../../utils/mediaLibrary'
  import type {
    CustomMetadataResponse,
    FieldIdentifier,
    FileTypeStateObject,
    FormValidationObject,
    SearchQueryJson,
  } from '../../interfaces'
  import AddFilterMenu from './AddFilterMenu'
  import FileTypeFilter from './Search/FileTypeFilter'
  import FilterGroupNew from './Search/FilterGroup'

  interface Props {
    page?: 'media-library' | 'media-collection'
    searchByTags: boolean
    setSearchByTags(searchByTags: boolean): void
    isVersionIncludedInSearch: boolean
    setIsVersionIncludedInSearch(isVersionIncludedInSearch: boolean): void
    searchQueryJson: SearchQueryJson
    customMetadataFields: CustomMetadataResponse[]
    customMetadataFieldsMapping: { [fieldName: string]: CustomMetadataResponse }
    validationObject: FormValidationObject
    maxWidthRef: RefObject<any>
    exposeVersionSettingOnly?: boolean
    fileType: FileTypeStateObject
    setFileType(fileType: FileTypeStateObject): void
    setSearchQueryJson(SearchQueryJson: SearchQueryJson): void
  }

  const AdvancedSearch = (props: Props): React.ReactElement => {
    const {
      searchByTags,
      setSearchByTags,
      isVersionIncludedInSearch,
      setIsVersionIncludedInSearch,
      searchQueryJson,
      setSearchQueryJson,
      validationObject,
      customMetadataFields,
      customMetadataFieldsMapping,
      maxWidthRef,
      exposeVersionSettingOnly,
      fileType,
      setFileType,
      page = 'media-library',
    } = props

    const { t } = useTranslation()
    
    // State to track which filter should auto-open
    const [shouldAutoOpenFilterId, setShouldAutoOpenFilterId] = useState<number | undefined>(undefined)
    const [showScroll, setShowScroll] = useState(false)
    const wrapRef = useRef<HTMLDivElement>(null)
  
    useEffect(() => {
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const element = entry.target as HTMLElement
          const scrollHeight = element.scrollHeight
          const clientHeight = element.clientHeight
          const maxHeight = 33 * 3 + 16 // 3 rows + spacing
          
          // Check if content overflows the maxHeight
          setShowScroll(scrollHeight > maxHeight)
        }
      })
    
      if (wrapRef.current) {
        resizeObserver.observe(wrapRef.current)
      }
    
      return () => resizeObserver.disconnect()
    }, [searchQueryJson, validationObject])

    // Reset auto-open state after a short delay to allow the filter to open
    useEffect(() => {
      if (shouldAutoOpenFilterId !== undefined) {
        const timer = setTimeout(() => {
          setShouldAutoOpenFilterId(undefined)
        }, 100) // Small delay to ensure the filter opens
        return () => clearTimeout(timer)
      }
    }, [shouldAutoOpenFilterId])

    const removeFilter = useCallback(
      ({
        json,
        groupId,
        filterId,
      }: {
        json: SearchQueryJson
        groupId: number
        filterId: number
      }) => {
        // Use deep clone to ensure proper state updates and comparisons
        const newSearchQueryJSON = _.cloneDeep(json)
        newSearchQueryJSON.filterGroups[groupId].expressions.splice(filterId, 1)
        const totalFilters =
          newSearchQueryJSON.filterGroups[groupId].expressions.length
        if (totalFilters === 0) {
          newSearchQueryJSON.filterGroups.splice(groupId, 1)
        }
        setSearchQueryJson(newSearchQueryJSON)
      },
      [setSearchQueryJson],
    )

    const onAddFilter = useCallback(
      (fieldIdentifier: FieldIdentifier) => {
        if (fieldIdentifier.fieldName === 'fileType') {
          setFileType({ text: '', value: '' })
        } else {
          const newSearchQueryJson = _.cloneDeep(searchQueryJson)
          if (newSearchQueryJson.filterGroups.length === 0) {
            newSearchQueryJson.filterGroups.push({
              boolean: 'and',
              expressions: [],
            })
          }
          newSearchQueryJson.filterGroups[0].expressions.push(
            getDefaultExpression(fieldIdentifier),
          )
          setSearchQueryJson(newSearchQueryJson)
          
          // Set the newly added filter to auto-open
          const newFilterIndex = newSearchQueryJson.filterGroups[0].expressions.length - 1
          setShouldAutoOpenFilterId(newFilterIndex)
        }
      },
      [searchQueryJson, setSearchQueryJson, setFileType],
    )

    return (
      <Wrap
      margin="0"
      ref={wrapRef} 
      spacing={2}
      align="center"
      overflowY={showScroll ? "auto" : "hidden"}
      overflowX="hidden"
      maxHeight="calc(33px * 3 + 1.5rem)"
      py={1}
      width="full">
        <Checkbox
          border={isVersionIncludedInSearch ? 'solid 1px #cddcf7' : 'solid 1px #e1e4eb'}
          p="6px 8px"
          borderRadius="4px"
          h="33px"
          isChecked={isVersionIncludedInSearch}
          background = {isVersionIncludedInSearch ? '#f3f8ff' : '#fff'}
          onChange={e => void setIsVersionIncludedInSearch(e.target.checked)}
          sx={{
            '.chakra-checkbox__control': {
              _checked: {
                bg: 'f3f8ff',
                color: "white",
              },
            },
            '.chakra-checkbox__label': {
              fontWeight: 'normal',
            },
          }}
        >
          Include file versions
        </Checkbox>
        {!exposeVersionSettingOnly ? (
          <Checkbox
            border={searchByTags ? 'solid 1px #cddcf7' : 'solid 1px #e1e4eb'}
            p="6px 8px"
            borderRadius="4px"
              h="33px"
              background = {searchByTags ? '#f3f8ff' : '#fff'}
            isChecked={searchByTags}
            onChange={e => void setSearchByTags(e.target.checked)}
            sx={{
              '.chakra-checkbox__control': {
                _checked: {
                  bg: 'f3f8ff',
                  color: "white",
                },
              },
              '.chakra-checkbox__label': {
                fontWeight: 'normal',
              },
            }}
          >
            Search by tags
          </Checkbox>
        ) : null}
        {!exposeVersionSettingOnly ? (
          <WrapItem>
            <FileTypeFilter fileType={fileType} setFileType={setFileType} maxWidthRef={maxWidthRef} />
          </WrapItem>
        ) : (
          <></>
        )}
        {!exposeVersionSettingOnly ? (
          <>
            {searchQueryJson.filterGroups.map((filterGroup, index) => (
              <React.Fragment key={index}>
                <FilterGroupNew
                  page={page}
                  {...{
                    searchQueryJson,
                    setSearchQueryJson,
                    customMetadataFields,
                    customMetadataFieldsMapping,
                    validationObject,
                    groupId: index,
                    maxWidthRef,
                    wrapRef,
                    removeFilter,
                    shouldAutoOpenFilterId: index === 0 ? shouldAutoOpenFilterId : undefined,
                  }}
                />
              </React.Fragment>
            ))}

            <AddFilterMenu
              searchQueryJson={searchQueryJson}
              page={page}
              customMetadataFields={customMetadataFields}
              onAddFilter={onAddFilter}
            />
          </>
        ) : null}
      </Wrap>
    )
  }

  export default AdvancedSearch
